import { Flex, Form, Switch } from "antd";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { DefaultInput, CustomButton, BankSelect } from "ui";
import { BankDto, CreateBankAccount, NOTIFICATION_TYPE_ENUM } from "interfaces";
import { newBankDetails, NormalizedError } from "services";
import { useStores } from "stores";
import { useNotificationContext } from "providers";
import { _isNotLongerThan, _isOnlyNumbersWithLength, _isRequired, parseErrorMessage } from "utils";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";

export const AddBankDetailsModal = observer(() => {
  const { actionModals, user, bankAccountsStore } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const companyId = user.userInfo?.company_id;

  const [form] = Form.useForm();

  const addBankDetails = async () => {
    if (!companyId) return;
    setLoading(true);

    const values = form.getFieldsValue();
    const formData: CreateBankAccount = {
      ...values,
      isDefault: values.isDefault ? true : false,
    };

    await newBankDetails({ id: companyId }, formData)
      .then(() => {
        openNotification({
          message: "Реквизиты добавлены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        handleCloseModal();
        setTimeout(() => {
          bankAccountsStore.fetchBankAccounts(companyId);
        }, 1500);
      })
      .catch((e: NormalizedError) => {
        openNotification({
          description: parseErrorMessage(e) ?? "Не получилось добавить новые реквизиты, попробуйте позже",
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onBankDataReceived = (bankData: BankDto) => {
    form.setFieldsValue({
      bankName: bankData.bankName,
      bankRegistrationNumber: bankData.bankRegistrationNumber,
      correspondentAccount: bankData.correspondentAccount,
    });

    form.validateFields(["correspondentAccount", "bankRegistrationNumber", "bankName"]);
  };

  const handleCloseModal = () => {
    actionModals.closeLastModal();
  };

  return (
    <>
      <Flex vertical>
        <Form form={form} name="addBankDetails" onFinish={addBankDetails}>
          <Form.Item
            name="accountName"
            rules={[_isRequired("Название обязательно для ввода"), _isNotLongerThan(160)]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <DefaultInput placeholder="Короткое название для конкретного счета" disabled={loading} maxLength={160} />
          </Form.Item>

          <BankSelect
            inputProps={{
              disabled: loading,
            }}
            name="bik"
            onBankDataReceived={onBankDataReceived}
          />

          <Form.Item name="bankName" hidden />

          <Form.Item
            name="bankRegistrationNumber"
            rules={[_isRequired("Регистрационный номер КГРКО обязателен для ввода")]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <DefaultInput placeholder="Регистрационный номер кредитной организации в соответствии с КГРКО" disabled />
          </Form.Item>
          <Form.Item
            name="correspondentAccount"
            rules={[_isRequired("Корреспондентский счет обязателен для ввода"), _isOnlyNumbersWithLength(20)]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <DefaultInput placeholder="Корреспондентский счет" disabled={loading} maxLength={20} />
          </Form.Item>
          <Form.Item
            name="accountNumber"
            rules={[_isRequired("Расчетный счет обязателен для ввода"), _isOnlyNumbersWithLength(20)]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <DefaultInput placeholder="Расчетный счет" disabled={loading} maxLength={20} />
          </Form.Item>
          <Flex gap="small" align="center" className="margin-top-md margin-bottom-md">
            <Form.Item name="isDefault" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
            <span>Сделать счетом по умолчанию</span>
          </Flex>

          <Form.Item className="margin-top-md">
            <Flex gap="middle">
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="default"
                onClick={handleCloseModal}
                fullWidth
              >
                Отмена
              </CustomButton>
              <CustomButton htmlType="submit" disabled={loading} loading={loading} fullWidth>
                Добавить реквизиты
              </CustomButton>
            </Flex>
          </Form.Item>
        </Form>
      </Flex>
    </>
  );
});
